import { useMemo, useState, type ReactNode } from 'react';
import { BadgePercent, CreditCard, Layers, LineChart, Package, Tags, Users } from 'lucide-react';
import { PageHeader } from '@/components/shared/page-header';
import { LocationBadge } from '@/components/shared/location-badge';
import { SupernovaDatePicker } from '@/components/ui/date-picker/supernova-date-picker';
import type { DateRange } from '@/components/ui/date-picker/types';
import { civilFromInstant, instantFromCivil } from '@/components/ui/date-picker/date-utils';
import { useReportingTimeZone } from '@/hooks/use-reporting-timezone';
import { useAuthStore } from '@/stores/auth-store';
import { useLocationStore } from '@/stores/location-store';
import { formatCurrency, formatNumber } from '@/lib/utils';
import {
  BarList,
  CardSkeleton,
  CustomerMix,
  KpiCard,
  Panel,
  PaymentSplit,
  SalesBreakdown,
  SalesTrend,
} from './home-cards';
import { HOME_REPORT_IDS, useHomeAnalytics, type HomeRange, type TrendGranularity } from './use-home-analytics';

// ── range <-> picker bridge ────────────────────────────────────────────────
// The queries speak wall-clock `YYYY-MM-DDTHH:mm` strings that withLocalDateRange
// interprets in the reporting zone; the picker exchanges Date instants. Both
// sides use the same zone, so values round-trip without drifting. Mirrors the
// bridge on the reports page.
function toDate(value: string, tz: string): Date | null {
  if (!value) return null;
  const [datePart, timePart] = value.split('T');
  const [y, m, d] = datePart.split('-').map(Number);
  const [hh, mm] = (timePart ?? '00:00').split(':').map(Number);
  if (!y || !m || !d) return null;
  return instantFromCivil({ year: y, month: m, day: d, hour: hh || 0, minute: mm || 0, second: 0 }, tz);
}
function toInput(date: Date | null, tz: string, endOfDay: boolean): string {
  if (!date) return '';
  const c = civilFromInstant(date, tz);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${c.year}-${p(c.month)}-${p(c.day)}T${endOfDay ? '23:59' : '00:00'}`;
}

/** Card title per bucket size: hourly for a day, daily for a week, weekly for
 *  a month, monthly for a quarter or year (see trendGranularityFor). */
const TREND_TITLES: Record<TrendGranularity, string> = {
  hour: 'Sales Trends (Hourly)',
  day: 'Sales Trends (Daily)',
  week: 'Sales Trends (Weekly)',
  month: 'Sales Trends (Monthly)',
};

/** Today, matching the picker's own "Today" preset. The page opens on the
 *  current business day; the picker still offers every other day and range. */
function today(tz: string): HomeRange {
  const d = civilFromInstant(new Date(), tz);
  const p = (n: number) => String(n).padStart(2, '0');
  const day = `${d.year}-${p(d.month)}-${p(d.day)}`;
  return { dateFrom: `${day}T00:00`, dateTo: `${day}T23:59` };
}

/**
 * The POS Management home: the analytics dashboard the Home page shows where
 * POS Management is on. Who sees it (role, module, preference) is decided by
 * home-page.tsx; this component only renders the cards.
 */
export function PosHome({ switcher }: { switcher?: ReactNode }) {
  const { user } = useAuthStore();
  const { selectedLocationId } = useLocationStore();
  const { timeZone: reportTz } = useReportingTimeZone();

  const [range, setRange] = useState<HomeRange>(() => today(reportTz));
  const analytics = useHomeAnalytics(range, selectedLocationId);
  const {
    can,
    trendGranularity,
    kpis,
    series,
    trendSeries,
    paymentMethods,
    topByVolume,
    promotionRows,
    departmentRows,
    categoryRows,
    customerMix,
    breakdown,
    loading,
    promotionsEnabled,
  } = analytics;

  // "Welcome back" rather than a time-of-day greeting: the wording is part of
  // the app's public surface, asserted by the login smoke test in
  // qa-automation, and changing it bought nothing worth breaking that for.
  const firstName = user?.name?.split(' ')[0] || 'there';

  const paymentTotal = useMemo(() => paymentMethods.reduce((sum, r) => sum + r.total, 0), [paymentMethods]);

  // Empty-card headlines say "yet today" while the page is on today — the day
  // isn't over, so "nothing yet" is the honest reading — and "in this period"
  // for any closed range.
  const isToday = useMemo(() => {
    const t = today(reportTz);
    return range.dateFrom === t.dateFrom && range.dateTo === t.dateTo;
  }, [range, reportTz]);
  const when = isToday ? 'yet today' : 'in this period';

  const showSummary = can(HOME_REPORT_IDS.summary);
  const showSeries = can(HOME_REPORT_IDS.dailySales);
  const showTopItems = can(HOME_REPORT_IDS.topItems);
  const showDepartments = can(HOME_REPORT_IDS.departments);
  const showCategories = can(HOME_REPORT_IDS.categories);
  const showPromotions = can(HOME_REPORT_IDS.promotions) && promotionsEnabled;
  const showCustomerMix = can(HOME_REPORT_IDS.customerMix);
  const nothingVisible =
    !showSummary &&
    !showSeries &&
    !showTopItems &&
    !showDepartments &&
    !showCategories &&
    !showPromotions &&
    !showCustomerMix &&
    !!selectedLocationId;

  return (
    <div className="space-y-6">
      {/* One picker governs every card, and it sits beside the location chip so
          the two things scoping the page read as one control group. Location
          itself comes from the global switcher in the top bar, so the page
          deliberately has no location control of its own. */}
      <PageHeader title={`Welcome back, ${firstName}`} description="How the business is doing right now">
        <LocationBadge />
        {switcher}
        <SupernovaDatePicker
          mode="range"
          presetFirst
          portal
          // The trigger is 260px next to the location chip, which clipped
          // "08/16/2026 – 09/02/2026". Compact prose fits.
          compactDisplay
          autoApply={false}
          timezone={reportTz}
          className="w-[260px] max-w-full"
          value={{ start: toDate(range.dateFrom, reportTz), end: toDate(range.dateTo, reportTz) }}
          onApply={(v) => {
            const r = (v as DateRange | null) ?? { start: null, end: null };
            if (!r.start || !r.end) return;
            setRange({ dateFrom: toInput(r.start, reportTz, false), dateTo: toInput(r.end, reportTz, true) });
          }}
        />
      </PageHeader>

      {!selectedLocationId && (
        <div className="rounded-xl border border-border bg-white p-8 text-center text-sm text-muted-foreground">
          Choose a location from the switcher above to see its numbers.
        </div>
      )}

      {nothingVisible && (
        <div className="rounded-xl border border-border bg-white p-8 text-center text-sm text-muted-foreground">
          None of the reports behind this page are available for your role at this location.
        </div>
      )}

      {/* ── headline figures ─────────────────────────────────────────── */}
      {showSummary && (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {loading.summary || !kpis ? (
            <>
              <CardSkeleton height="h-36" />
              <CardSkeleton height="h-36" />
              <CardSkeleton height="h-36" />
              <CardSkeleton height="h-36" />
            </>
          ) : (
            <>
              <KpiCard
                label="Net Sales"
                value={formatCurrency(kpis.netSales.value)}
                delta={kpis.netSales.delta}
                previous={formatCurrency(kpis.netSales.delta.previous)}
                series={series}
                dataKey="sales"
              />
              {/* Orders drill into All Orders on the same period; the
                  link is withheld where that report is hidden for this role. */}
              <KpiCard
                label="Orders"
                value={formatNumber(kpis.transactions.value)}
                delta={kpis.transactions.delta}
                previous={formatNumber(kpis.transactions.delta.previous)}
                series={series}
                dataKey="transactions"
                reportId={can('all-orders') ? 'all-orders' : undefined}
                range={range}
              />
              <KpiCard
                label="Average Basket"
                value={formatCurrency(kpis.avgBasket.value)}
                delta={kpis.avgBasket.delta}
                previous={formatCurrency(kpis.avgBasket.delta.previous)}
                series={series}
                dataKey="avgBasket"
              />
              <KpiCard
                label="Refunds"
                value={formatCurrency(kpis.refunds.value)}
                delta={kpis.refunds.delta}
                previous={formatCurrency(kpis.refunds.delta.previous)}
                series={series}
                dataKey="refunds"
                invert
                footnote={`${formatNumber(kpis.refunds.count)} refunds`}
                reportId={can('refunds') ? 'refunds' : undefined}
                range={range}
              />
            </>
          )}
        </div>
      )}

      {/* ── trend + payment mix ──────────────────────────────────────── */}
      {(showSeries || showSummary) && (
        <div className="grid gap-4 lg:grid-cols-2">
          {/* Drill into the Sales Trends report, which keeps this range and
              grouping; fall back to Total Daily Sales where that report is
              hidden for this role, so the link never lands on an empty page. */}
          {showSeries && (
            <Panel
              title={TREND_TITLES[trendGranularity]}
              reportId={can('sales-trends') ? 'sales-trends' : 'total-daily-sales'}
              range={range}
            >
              {loading.series ? (
                <CardSkeleton height="h-56" />
              ) : (
                <SalesTrend
                  series={trendSeries}
                  empty={{
                    icon: LineChart,
                    title: `No sales ${when}`,
                    description: 'The trend fills in as orders come through the POS.',
                  }}
                />
              )}
            </Panel>
          )}
          {showSummary && (
            <Panel title="Payment Types" reportId="sales-summary-current-location" range={range}>
              {loading.summary ? (
                <CardSkeleton height="h-40" />
              ) : (
                <PaymentSplit
                  rows={paymentMethods}
                  total={paymentTotal}
                  empty={{
                    icon: CreditCard,
                    title: `No payments ${when}`,
                    description: 'Card, cash, and other tenders appear here as a split.',
                  }}
                />
              )}
            </Panel>
          )}
        </div>
      )}

      {/* ── composition ──────────────────────────────────────────────── */}
      {(showTopItems || showDepartments) && (
        <div className="grid gap-4 lg:grid-cols-2">
          {showTopItems && (
            <Panel title="Top Items by Volume" reportId="top-items-by-sales-volume" range={range}>
              {loading.topItems ? (
                <CardSkeleton height="h-40" />
              ) : (
                <BarList
                  empty={{
                    icon: Package,
                    title: `No items sold ${when}`,
                    description: 'Your best-sellers rank here by units sold.',
                  }}
                  rows={topByVolume.map((r) => ({
                    id: r.productId,
                    name: r.productName,
                    value: `${formatNumber(r.unitsSold)} units`,
                    share: r.unitsSold,
                  }))}
                />
              )}
            </Panel>
          )}
          {showDepartments && (
            <Panel title="Sales by Department" reportId="total-daily-department-sales" range={range}>
              {loading.departments ? (
                <CardSkeleton height="h-40" />
              ) : (
                <BarList
                  empty={{
                    icon: Layers,
                    title: `No department sales ${when}`,
                    description: 'Sales roll up here by department as items sell.',
                  }}
                  rows={departmentRows.map((r) => ({
                    id: r.departmentId ?? r.departmentName,
                    name: r.departmentName,
                    value: formatCurrency(r.grossSale),
                    share: r.grossSale,
                  }))}
                />
              )}
            </Panel>
          )}
        </div>
      )}

      {(showCategories || showSummary) && (
        <div className="grid gap-4 lg:grid-cols-2">
          {showCategories && (
            <Panel title="Sales by Category" reportId="total-daily-category-sales" range={range}>
              {loading.categories ? (
                <CardSkeleton height="h-40" />
              ) : (
                <BarList
                  empty={{
                    icon: Tags,
                    title: `No category sales ${when}`,
                    description: 'Sales roll up here by category as items sell.',
                  }}
                  rows={categoryRows.map((r) => ({
                    id: r.categoryId ?? r.categoryName,
                    name: r.categoryName,
                    value: formatCurrency(r.grossSale),
                    share: r.grossSale,
                  }))}
                />
              )}
            </Panel>
          )}
          {showSummary && (
            <Panel
              title="Sales Breakdown"
              reportId="sales-summary-current-location"
              range={range}
              hint="View Details opens the Sales Summary for the currently selected location."
            >
              {loading.summary || !breakdown ? (
                <CardSkeleton height="h-40" />
              ) : (
                <SalesBreakdown
                  rows={[
                    { label: 'Subtotal', value: breakdown.subtotal },
                    { label: 'Discounts', value: breakdown.discount, negative: true },
                    { label: 'Tax', value: breakdown.tax },
                    { label: 'Tips', value: breakdown.tips },
                    { label: 'Net Sales', value: breakdown.net, emphasis: true },
                    { label: 'Net after refunds', value: breakdown.netAfterRefunds, emphasis: true },
                  ]}
                />
              )}
            </Panel>
          )}
        </div>
      )}

      {(showPromotions || showCustomerMix) && (
        <div className="grid gap-4 lg:grid-cols-2">
          {showPromotions && (
            <Panel title="Top Promotions Used" reportId="promotion-usage" range={range}>
              {loading.promotions ? (
                <CardSkeleton height="h-40" />
              ) : (
                <BarList
                  empty={{
                    icon: BadgePercent,
                    title: `No promotions redeemed ${when}`,
                    description: 'Promotions applied at checkout rank here by uses.',
                  }}
                  rows={promotionRows.map((r) => ({
                    id: r.promotionId,
                    name: r.promotionName ?? 'Unnamed promotion',
                    value: `${formatNumber(r.totalUses)} uses`,
                    share: r.totalUses,
                  }))}
                />
              )}
            </Panel>
          )}
          {/* No `reportId`: the split isn't any one report, so there's nowhere
              single to drill into. */}
          {showCustomerMix && (
            <Panel title="New vs Returning Customers">
              {loading.customerMix || !customerMix ? (
                <CardSkeleton height="h-40" />
              ) : (
                <CustomerMix
                  newCustomers={customerMix.newCustomers}
                  returningCustomers={customerMix.returningCustomers}
                  anonymousSales={customerMix.anonymousSales}
                  empty={{
                    icon: Users,
                    title: `No identified customers ${when}`,
                    description: 'Attach a customer at checkout to see new vs returning.',
                  }}
                />
              )}
            </Panel>
          )}
        </div>
      )}
    </div>
  );
}
